import React, { useEffect, useState } from "react";

import {
  Button,
  message,
  Popconfirm,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import moment from "moment";

import { ReloadOutlined } from "@ant-design/icons";

import CustomSearchbar from "../../../Components/CustomSearchbar/CustomSearchbar";
import CustomSelect from "../../../Components/CustomSelect/CustomSelect";
import TableUserInfoItem from "../../../Components/TableUserInfoItem/TableUserInfoItem";
import { DATE_FORMAT, PAGE_LIMIT } from "../../../Utils/constants";
import { showAppError } from "../../../Utils/error";
import { KYCVerificationStatus } from "../../../Utils/types";
import { updateUserDetails } from "../../api";
import UserAPI from "../../api/UserAPI";

const STATUS_CONTENT_MAP = {
  [KYCVerificationStatus.DETAILS_ADDED]: {
    color: "processing",
    text: "Details Added",
  },
  [KYCVerificationStatus.UNDER_REVIEW]: {
    color: "warning",
    text: "Under Review",
  },
  [KYCVerificationStatus.VERIFIED]: {
    color: "success",
    text: "Verified",
  },
  [KYCVerificationStatus.REJECTED]: {
    color: "error",
    text: "Rejected",
  },
};

const initialState = {
  loading: false,
  list: [],
  page: 1,
  limit: PAGE_LIMIT,
};

const initialFilters = {
  search: "",
  // FIRC eligibility is typically granted to KYC-verified creators
  status: KYCVerificationStatus.VERIFIED,
};

/**
 * FIRC eligibility management inside the Manage KYC section (TMG-2249).
 * Lists creators (by KYC status, like the KYC queue) and lets an admin toggle
 * `eligibleForFIRC` — the gate that shows the "Export Earnings Compliance"
 * section to a creator so they can submit their LUT.
 */
const FIRCEligibilityList = () => {
  const [state, setState] = useState(initialState);
  const [filters, setFilters] = useState(initialFilters);
  const [togglingId, setTogglingId] = useState("");

  const onChangeFilters = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const fetchList = async () => {
    const newState = { ...initialState, ...state };

    newState.loading = true;
    setState({ ...newState });

    try {
      const resp = await UserAPI.getUserKYCList({
        page: state.page,
        limit: state.limit,
        search: filters.search,
        status: filters.status,
        // only creators FIRC can apply to — Indian creators on their own
        // whitelabel host (TMG-3495)
        fircOnly: true,
      });

      if (resp?.code === 200) {
        newState.list = [...(resp.result || [])].map((item) => ({
          ...item,
          key: item._id,
        }));
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      newState.loading = false;
      setState({ ...newState });
    }
  };

  const reloadList = () => {
    setState((prev) => ({ ...prev, list: [] }));
    fetchList();
  };

  const toggleEligibility = async (record) => {
    const nextValue = !record?.eligibleForFIRC;

    setTogglingId(record?._id);

    try {
      const resp = await updateUserDetails({
        creatorId: record?._id,
        eligibleForFIRC: nextValue,
      });

      // legacy /admin/update_user_details responds with code 0 on success
      if (resp?.code === 0) {
        message.success(
          `FIRC eligibility ${nextValue ? "enabled" : "disabled"} for ${
            record?.name || "creator"
          }`
        );
        // reflect the change locally without refetching the whole list
        setState((prev) => ({
          ...prev,
          list: prev.list.map((item) =>
            item._id === record._id
              ? { ...item, eligibleForFIRC: nextValue }
              : item
          ),
        }));
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setTogglingId("");
    }
  };

  useEffect(() => {
    reloadList();
  }, [state.page, state.limit, filters]);

  const columns = [
    {
      title: "Submitted On",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) => moment(date || new Date()).format(DATE_FORMAT),
    },
    {
      title: "User Info",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <TableUserInfoItem
          name={record?.name}
          profilePic={record?.profilePicUrl}
          email={record?.email}
          phone={record?.phone}
          country={record?.country}
          dialCode={record?.dialCode}
          isCopyable
        />
      ),
    },
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
    },
    {
      title: "KYC Status",
      dataIndex: "verificationStatus",
      key: "verificationStatus",
      render: (verificationStatus) => (
        <Tag color={STATUS_CONTENT_MAP?.[verificationStatus]?.color}>
          {STATUS_CONTENT_MAP?.[verificationStatus]?.text || verificationStatus}
        </Tag>
      ),
    },
    {
      title: "LUT Status",
      dataIndex: "lutVerificationStatus",
      key: "lutVerificationStatus",
      render: (lutVerificationStatus) =>
        lutVerificationStatus ? (
          <Tag color={STATUS_CONTENT_MAP?.[lutVerificationStatus]?.color}>
            {STATUS_CONTENT_MAP?.[lutVerificationStatus]?.text ||
              lutVerificationStatus}
          </Tag>
        ) : (
          <Typography.Text type="secondary">Not submitted</Typography.Text>
        ),
    },
    {
      title: "Eligible for FIRC",
      dataIndex: "eligibleForFIRC",
      key: "eligibleForFIRC",
      fixed: "right",
      render: (eligibleForFIRC, record) => (
        <Popconfirm
          title={`${
            eligibleForFIRC ? "Disable" : "Enable"
          } FIRC export-earnings compliance for ${
            record?.name || "this creator"
          }?`}
          okText="Yes"
          cancelText="No"
          onConfirm={() => toggleEligibility(record)}
          disabled={togglingId === record?._id}
        >
          <Switch
            checked={Boolean(eligibleForFIRC)}
            loading={togglingId === record?._id}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <div
        className="filters-wrapper kyc-list__filters"
        style={{
          paddingBlock: 16,
          paddingTop: 0,
        }}
      >
        <CustomSearchbar
          placeholder="Search by name, email or phone"
          searchQuery={filters.search}
          setSearchQuery={(value) => onChangeFilters("search", value)}
          style={{ marginBottom: 0, maxWidth: 500 }}
        />

        <Tooltip title="Refresh">
          <Button size="large" onClick={reloadList}>
            <ReloadOutlined />
          </Button>
        </Tooltip>

        <CustomSelect
          itemsList={Object.values(KYCVerificationStatus)
            .filter((item) => item !== KYCVerificationStatus.DETAILS_ADDED)
            .map((value) => ({
              _id: value,
              title: value.replace(/_/g, " ").toTitleCase(),
            }))}
          item={filters.status}
          placeholder="Select KYC status"
          onSelectItem={(value) => onChangeFilters("status", value)}
          hasSelectAll={false}
          style={{
            width: 250,
            marginLeft: "auto",
          }}
        />
      </div>

      <Table
        key="fircEligibilityList"
        columns={columns}
        dataSource={state.loading ? [] : state.list}
        pagination={{
          current: state.page,
          showSizeChanger: false,
          pageSize: state.limit,
          onChange: (p) => setState((prev) => ({ ...prev, page: p })),
        }}
        loading={state.loading}
        rowKey={(record) => record._id}
        locale={{
          emptyText:
            (state.loading && "Loading...") ||
            ((filters.search || filters.status) &&
              "Sorry, we didn't find any matching results") ||
            "No creators found",
        }}
        scroll={{ x: "auto" }}
      />
    </>
  );
};

export default FIRCEligibilityList;
